import { mergeProps } from '@base-ui/react/merge-props';
import { Popover } from '@base-ui/react/popover';
import { type RefObject } from 'react';

type TriggerRender = Extract<Popover.Trigger.Props['render'], (...args: never[]) => unknown>;

type SplitTriggerPropsParams = {
    isOpen: boolean;
    onClose: () => void;
    onKeyboardOpen: () => void;
    openOnArrowDown?: boolean;
    props: Parameters<TriggerRender>[0];
    toggleRef: RefObject<HTMLButtonElement | null>;
};

export function splitTriggerProps({
    isOpen,
    onClose,
    onKeyboardOpen,
    openOnArrowDown,
    props,
    toggleRef,
}: SplitTriggerPropsParams) {
    const {
        ref,
        role: _role,
        tabIndex,
        onClick,
        onKeyDown,
        onKeyUp,
        onMouseDown,
        onPointerDown,
        'aria-controls': ariaControls,
        'aria-expanded': ariaExpanded,
        ...groupProps
    } = props;

    return {
        groupProps: mergeProps<'div'>(groupProps, {
            ref,
            role: 'group',
            tabIndex: -1,
            onFocus(event) {
                if (event.target === event.currentTarget) {
                    toggleRef.current?.focus();
                }
            },
        }),
        buttonProps: mergeProps<'button'>(
            {
                ref: toggleRef,
                tabIndex,
                'aria-controls': ariaControls,
                'aria-expanded': ariaExpanded,
                'aria-label': 'Toggle account menu',
                onClick,
                onKeyDown,
                onKeyUp,
                onMouseDown,
                onPointerDown,
            },
            {
                onClick(event) {
                    if (isOpen) {
                        event.preventBaseUIHandler();
                        onClose();
                    }
                },
                onKeyDown(event) {
                    if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
                        onKeyboardOpen();
                    }

                    if (event.key !== 'ArrowDown' || !openOnArrowDown) {
                        return;
                    }

                    event.preventDefault();

                    if (isOpen) {
                        return;
                    }

                    event.currentTarget.click();
                },
            }
        ),
    };
}
